import type { DefineComponent, VNode } from 'vue'

import { createInertiaApp, Link } from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { createPinia } from 'pinia'
import { createApp, h } from 'vue'
import { ZiggyVue } from 'ziggy-js'

import '@/assets/fonts/pjazza-symbols.css'
import { PJ_SKELETON_ID } from '@/domains/pjazza/types/consts/AppContext.consts.ts'
import { installQueryPlugin } from '@/support/query/installQueryPlugin'

import OrderLayout from './layouts/OrderLayout.vue'

/**
 * The consumer app.
 *
 * No PrimeVue and no shared design-system theme. The Pjazza handoff builds
 * everything from its own tokens, and PrimeVue components arrive wrapped in a
 * container with a border and an elevation — the three things rule 1 forbids.
 * Icons come from Pjazza's own font, which carries only the `PJ_ICON` set.
 */
const pinia = createPinia()

/**
 * Which shell each page gets.
 *
 * 'pjazza' is the two-tab context — Places and You. 'venue' is inside a store,
 * where the only navigation is the venue's own bar with a back arrow. 'bare'
 * is the screens that should carry nothing at all: the camera, and the ready
 * screen where the order number is the only thing that matters.
 */
const LAYOUTS: Record<string, { context: 'pjazza' | 'venue' | 'bare'; hasOrderMode?: boolean }> = {
  'Directory/Index': { context: 'pjazza' },
  'You/Show': { context: 'pjazza' },
  'Menu/Show': { context: 'venue' },
  'Checkout/Show': { context: 'venue' },
  'Book/Show': { context: 'venue', hasOrderMode: false },
  'Tab/Show': { context: 'venue' },
  'OrderStatus/Show': { context: 'venue' },
  'Payment/Confirming': { context: 'venue' }
}

void createInertiaApp({
  // Consumer pages own their full <title> via server meta — no " - Twine" suffix.
  title: title => title,
  resolve: async name => {
    const page = await resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob<DefineComponent>('./Pages/**/*.vue')
    )

    // Layout choice lives here rather than in each page: which shell a screen
    // gets is a property of the information architecture, and reading it in
    // one list is how it stays consistent.
    //
    // Inertia invokes a function layout as `layout(h, page)` — both arguments
    // are required. Taking only one binds `h` to the child and renders the
    // function's own source into the slot.
    const layoutProps = LAYOUTS[name] ?? { context: 'bare' as const }

    page.default.layout =
      page.default.layout ??
      ((createElement: typeof h, child: VNode) => createElement(OrderLayout, layoutProps, () => child))

    return page
  },
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) })

    app.use(plugin).use(pinia).use(ZiggyVue).component('Link', Link)

    installQueryPlugin(app)
    app.mount(el)

    // The server's skeleton of a venue page stands in until this first render.
    // Mounting draws the page synchronously, so it goes before the next paint.
    document.getElementById(PJ_SKELETON_ID)?.remove()
  },
  progress: {
    color: 'var(--pj-pink)'
  }
})
